import { useEffect } from 'react';
import type { SurveyPhase } from '../types/survey';

interface KioskControlOptions {
  phase: SurveyPhase;
  operatorOpen: boolean;
  onOpenOperator: () => void;
  onCloseOperator: () => void;
  onReset: () => void;
  onStart: () => void;
  onAnswer: (position: number) => void;
}

export function useKioskControls({
  phase,
  operatorOpen,
  onOpenOperator,
  onCloseOperator,
  onReset,
  onStart,
  onAnswer,
}: KioskControlOptions) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const activeTag = document.activeElement?.tagName ?? '';
      const isFormField = ['INPUT', 'SELECT', 'TEXTAREA'].includes(activeTag);

      if ((event.ctrlKey || event.metaKey) && ['+', '-', '=', '0'].includes(event.key)) {
        event.preventDefault();
      }

      if (event.key === 'Backspace' && !isFormField) {
        event.preventDefault();
      }

      if (event.key === 'F10' || (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'o') || (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'o')) {
        event.preventDefault();
        onOpenOperator();
        return;
      }

      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'x') {
        event.preventDefault();
        onReset();
        return;
      }

      if (event.key === 'Escape' && operatorOpen) {
        event.preventDefault();
        onCloseOperator();
        return;
      }

      const key = event.code.startsWith('Numpad')
        ? event.code.replace('Numpad', '')
        : event.key;

      if (!['1', '2', '3'].includes(key) || operatorOpen || isFormField) return;

      event.preventDefault();
      if (phase === 'question') {
        onAnswer(Number(key));
      } else if (phase === 'start' || (phase === 'result' && key === '1')) {
        onStart();
      }
    }

    function preventContextMenu(event: MouseEvent) {
      event.preventDefault();
    }

    function preventZoomWheel(event: WheelEvent) {
      if (event.ctrlKey) event.preventDefault();
    }

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('contextmenu', preventContextMenu);
    window.addEventListener('wheel', preventZoomWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('contextmenu', preventContextMenu);
      window.removeEventListener('wheel', preventZoomWheel);
    };
  }, [
    onAnswer,
    onCloseOperator,
    onOpenOperator,
    onReset,
    onStart,
    operatorOpen,
    phase,
  ]);
}
